import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { GetAllSubnetsResponseItem } from "src/api/generated/frontend_internal_v1";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Textarea } from "src/components/base/textarea";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";

/**
 * The properties for {@link UpdateSubnetDialog}
 */
export type UpdateSubnetDialogProps = {
    /** The subnet to update */
    subnet: GetAllSubnetsResponseItem | undefined;
    /** Whether the dialog is open */
    isOpen: boolean;
    /** The callback when the dialog is submitted */
    onSubmit: () => void;
    /** The callback when the dialog is closed */
    onClose: () => void;
};

/**
 * Dialog to update a subnet
 */
export default function UpdateSubnetDialog(props: UpdateSubnetDialogProps) {
    const [t] = useTranslation("dialog-update-subnet");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            description: props.subnet?.description ?? "",
        },
        onSubmit: async ({ value }) => {
            if (!props.subnet) return;
            await ApiInternal.subnets.setDescription(props.subnet.uuid, value.description);
            props.onSubmit();
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.subnet]);

    if (!props.subnet) return null;

    return (
        <Dialog onClose={props.onClose} open={props.isOpen}>
            <DialogTitle>{t("heading.edit-subnet")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.description")}</Label>
                                        <Textarea
                                            rows={4}
                                            maxLength={1024}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            value={fieldApi.state.value}
                                        />
                                    </Field>
                                )}
                            </form.Field>
                            <DialogActions>
                                <Button onClick={props.onClose}>{tg("button.cancel")}</Button>
                                <PrimaryButton type={"submit"}>{t("button.update")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
